/**
 * FANPLAZA — Scroll Reveal Web Component
 *
 * Usage in Liquid:
 *   <scroll-reveal>Content fades up on scroll</scroll-reveal>
 *   <scroll-reveal data-effect="fade">Fade only, no movement</scroll-reveal>
 *   <scroll-reveal data-effect="slide-left">Slide from left</scroll-reveal>
 *   <scroll-reveal data-stagger>Children animate one by one</scroll-reveal>
 *   <scroll-reveal data-delay="200">Delayed reveal</scroll-reveal>
 */
class ScrollReveal extends HTMLElement {
  connectedCallback() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      this.classList.add('is-visible');
      return;
    }

    const delay = parseInt(this.dataset.delay) || 0;
    const threshold = parseFloat(this.dataset.threshold) || 0.1;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              this.classList.add('is-visible');
            }, delay);
            this.observer.unobserve(this);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

if (!customElements.get('scroll-reveal')) {
  customElements.define('scroll-reveal', ScrollReveal);
}

/**
 * FANPLAZA — Subtle Parallax
 *
 * Usage: <div class="fp-parallax" data-speed="0.15">
 * Speed: 0 = no movement, 0.3 = strong parallax
 */
(function() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const els = [];
  let ticking = false;

  function init() {
    document.querySelectorAll('.fp-parallax').forEach(el => {
      els.push({ el, speed: parseFloat(el.dataset.speed) || 0.1 });
    });
    if (els.length) window.addEventListener('scroll', onScroll, { passive: true });
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }

  function update() {
    const scrollY = window.scrollY;
    const vh = window.innerHeight;
    els.forEach(({ el, speed }) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < vh) {
        const center = rect.top + rect.height / 2 - vh / 2;
        el.style.transform = 'translateY(' + (center * speed * -1).toFixed(1) + 'px)';
      }
    });
    ticking = false;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * FANPLAZA — Smooth cursor-follow for product images
 *
 * Usage: <div class="fp-tilt" data-intensity="8">
 * Creates a subtle 3D tilt on mouse move
 */
(function() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if ('ontouchstart' in window) return;

  /* Cache tilt elements once — don't query on every mousemove */
  var tiltEls = [];
  function cacheTiltEls() {
    tiltEls = Array.from(document.querySelectorAll('.fp-tilt'));
  }

  function initTilt() {
    cacheTiltEls();
    if (!tiltEls.length) return;

    var ticking = false;
    var lastE = null;

    document.addEventListener('mousemove', function(e) {
      lastE = e;
      if (!ticking) {
        requestAnimationFrame(function() {
          tiltEls.forEach(function(el) {
            var rect = el.getBoundingClientRect();
            if (lastE.clientY < rect.top || lastE.clientY > rect.bottom ||
                lastE.clientX < rect.left || lastE.clientX > rect.right) {
              el.style.transform = '';
              return;
            }
            var intensity = parseFloat(el.dataset.intensity) || 6;
            var x = (lastE.clientX - rect.left) / rect.width - 0.5;
            var y = (lastE.clientY - rect.top) / rect.height - 0.5;
            el.style.transform = 'perspective(600px) rotateY(' + (x * intensity).toFixed(2) + 'deg) rotateX(' + (y * -intensity).toFixed(2) + 'deg)';
          });
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTilt);
  } else {
    initTilt();
  }
})();
(function () {
  'use strict';

  function initCard(card) {
    var images = Array.from(card.querySelectorAll('.fp-product-card__image'));
    var dots   = Array.from(card.querySelectorAll('.fp-product-card__dot'));
    var prev   = card.querySelector('.fp-product-card__arrow--prev');
    var next   = card.querySelector('.fp-product-card__arrow--next');

    if (images.length < 2) return;

    var current = 0;

    function goTo(index) {
      var n = images.length;
      index = Math.max(0, Math.min(index, n - 1));

      images[current].classList.remove('fp-product-card__image--active');
      if (dots[current]) dots[current].classList.remove('fp-product-card__dot--active');

      current = index;

      images[current].classList.add('fp-product-card__image--active');
      if (dots[current]) dots[current].classList.add('fp-product-card__dot--active');

      if (prev) prev.disabled = current === 0;
      if (next) next.disabled = current === n - 1;
    }

    if (prev) {
      prev.addEventListener('click', function (e) { e.preventDefault(); goTo(current - 1); });
    }
    if (next) {
      next.addEventListener('click', function (e) { e.preventDefault(); goTo(current + 1); });
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function (e) { e.preventDefault(); goTo(i); });
    });

    goTo(0);
  }

  function initAll() {
    document.querySelectorAll('.fp-product-card--has-gallery').forEach(initCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Re-init after Shopify section re-renders in Theme Editor */
  document.addEventListener('shopify:section:load', function () {
    initAll();
  });
})();
/**
 * FANPLAZA Wishlist System
 * localStorage-based, works for guests and logged-in users
 * Features: toggle, sync UI, toast feedback, cross-tab sync, mutation observer
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'fp_wishlist';
  var OLD_KEY = 'rpt_wishlist';
  var EVENT_CHANGED = 'fanplaza:wishlist:changed';
  var MAX_ITEMS = 50;

  class FanPlazaWishlist {
    constructor() {
      this._items = this._load();
      this._bindEvents();
      this._syncUI();
    }

    /* ── Storage ── */

    _load() {
      try {
        /* Migrate from old key if needed */
        var raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
          var old = localStorage.getItem(OLD_KEY);
          if (old) {
            localStorage.setItem(STORAGE_KEY, old);
            localStorage.removeItem(OLD_KEY);
            raw = old;
          }
        }
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        return [];
      }
    }

    _save() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this._items));
      } catch (e) { /* storage full */ }
      document.dispatchEvent(new CustomEvent(EVENT_CHANGED, { detail: { items: this._items } }));
      this._syncUI();
    }

    /* ── Public API ── */

    add(handle) {
      if (!handle || this.has(handle)) return;
      this._items.push(handle);
      if (this._items.length > MAX_ITEMS) this._items.shift();
      this._save();
      this._showToast('Added to Wishlist');
    }

    remove(handle) {
      var idx = this._items.indexOf(handle);
      if (idx === -1) return;
      this._items.splice(idx, 1);
      this._save();
      this._showToast('Removed from Wishlist');
    }

    toggle(handle) {
      this.has(handle) ? this.remove(handle) : this.add(handle);
    }

    has(handle) {
      return this._items.indexOf(handle) !== -1;
    }

    getAll() {
      return this._items.slice();
    }

    count() {
      return this._items.length;
    }

    /* ── UI Sync ── */

    _syncUI() {
      var self = this;

      /* Update all bookmark buttons */
      document.querySelectorAll('[data-fp-bookmark]').forEach(function (btn) {
        var handle = btn.getAttribute('data-product-handle');
        if (!handle) return;
        var saved = self.has(handle);
        btn.classList.toggle('is-saved', saved);
        btn.setAttribute('aria-pressed', saved ? 'true' : 'false');

        /* Update label text if present */
        var label = btn.querySelector('.fp-bookmark__label');
        if (label) label.textContent = saved ? 'Saved' : 'Save to Wishlist';
      });

      /* Update header counters */
      var c = self.count();
      document.querySelectorAll('[data-fp-wishlist-count]').forEach(function (el) {
        el.textContent = c > 0 ? c : '';
        el.style.display = c > 0 ? '' : 'none';
      });
    }

    /* ── Toast Notification ── */

    _showToast(message) {
      var existing = document.querySelector('.fp-toast');
      if (existing) existing.remove();

      var toast = document.createElement('div');
      toast.className = 'fp-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
        '<span>' + message + '</span>';
      document.body.appendChild(toast);

      requestAnimationFrame(function () {
        toast.classList.add('is-visible');
      });

      setTimeout(function () {
        toast.classList.remove('is-visible');
        setTimeout(function () { toast.remove(); }, 350);
      }, 2500);
    }

    /* ── Event Binding ── */

    _bindEvents() {
      var self = this;

      /* Click delegation for all bookmark buttons */
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-fp-bookmark]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        var handle = btn.getAttribute('data-product-handle');
        if (handle) self.toggle(handle);
      });

      /* Cross-tab sync via storage event */
      window.addEventListener('storage', function (e) {
        if (e.key === STORAGE_KEY) {
          self._items = self._load();
          self._syncUI();
        }
      });

      /* Re-sync after AJAX / section rendering — debounced */
      var syncTimer = null;
      var observer = new MutationObserver(function () {
        if (syncTimer) return;
        syncTimer = setTimeout(function () {
          syncTimer = null;
          self._syncUI();
        }, 300);
      });
      observer.observe(document.body, { childList: true });
    }
  }

  /* Initialise */
  function init() {
    window.FanPlazaWishlist = new FanPlazaWishlist();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
